import { useId, useState, type FormEvent } from 'react';
import type { TicketUrgency } from '@brimo/shared';
import {
  Banner,
  Button,
  Dialog,
  Segmented,
  Select,
  TextField,
  Textarea,
  type ComboboxOption,
} from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { createTicket, useInvalidateTickets } from '../api';
import type { Named } from '../types';
import { ContactPicker } from './ContactPicker';
import styles from './create.module.css';

export interface CreateTicketDialogProps {
  categories: Named[];
  /** Called with the new ticket's id after „Ticket anlegen", without it on „Abbrechen". */
  onClose: (createdId?: string) => void;
}

/**
 * `/tickets/new` — „Ticket anlegen" for a phone request (no frame; own texts): contact, subject, message, category and
 * urgency → `POST /tickets` (channel „Telefon"), then the record opens.
 */
export function CreateTicketDialog({ categories, onClose }: CreateTicketDialogProps) {
  const formId = useId();
  const invalidate = useInvalidateTickets();
  const [contact, setContact] = useState<ComboboxOption | null>(null);
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [urgency, setUrgency] = useState<TicketUrgency>('normal');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (!contact) next['contactId'] = 'Bitte einen Kontakt wählen.';
    if (!title.trim()) next['title'] = 'Bitte einen Betreff angeben.';
    if (!text.trim()) next['text'] = 'Bitte einen Text eingeben.';
    setErrors(next);
    if (Object.keys(next).length > 0 || !contact) return;
    setPending(true);
    setBanner(null);
    try {
      const created = await createTicket({
        contactId: contact.id,
        title: title.trim(),
        text: text.trim(),
        categoryId: categoryId || null,
        urgency,
      });
      await invalidate();
      onClose(created.id);
    } catch (error) {
      if (error instanceof ApiError && Object.keys(error.fields).length > 0) setErrors(error.fields);
      else setBanner(errorMessage(error));
      setPending(false);
    }
  }

  return (
    <Dialog
      open
      onClose={() => onClose()}
      width={600}
      initialFocus="none"
      eyebrow="Tickets"
      title="Ticket anlegen"
      subtitle="Für eine Anfrage per Telefon oder im Gespräch. Das Ticket erscheint in der Spalte „Neu“."
      footer={
        <>
          <Button variant="secondary" onClick={() => onClose()} disabled={pending}>
            Abbrechen
          </Button>
          <Button type="submit" form={formId} loading={pending}>
            Ticket anlegen
          </Button>
        </>
      }
    >
      <form id={formId} className={styles.form} onSubmit={(event) => void submit(event)} noValidate>
        {banner ? <Banner tone="danger">{banner}</Banner> : null}
        <ContactPicker value={contact} onChange={setContact} error={errors['contactId']} autoFocus />
        <TextField
          label="Betreff"
          required
          value={title}
          maxLength={200}
          onChange={(event) => setTitle(event.target.value)}
          error={errors['title']}
        />
        <Textarea
          label="Nachricht"
          required
          rows={4}
          autoGrow
          value={text}
          onChange={(event) => setText(event.target.value)}
          error={errors['text']}
          hint="Was der Kunde schildert. Der Text steht als erste Nachricht im Ticket."
        />
        <div className={styles.row}>
          <Select
            label="Kategorie"
            optional
            placeholder="Ohne Kategorie"
            value={categoryId}
            onChange={(event) => setCategoryId(event.target.value)}
            options={categories.map((category) => ({ value: category.id, label: category.name }))}
            error={errors['categoryId']}
          />
          <div className={styles.segmentField}>
            <span className={styles.segmentLabel}>Dringlichkeit</span>
            <Segmented
              label="Dringlichkeit"
              value={urgency}
              onChange={setUrgency}
              items={[
                { value: 'normal', label: 'normal' },
                { value: 'urgent', label: 'eilig' },
              ]}
            />
          </div>
        </div>
      </form>
    </Dialog>
  );
}
